import type { EditorPanel, EditorPanelStateChangeCallback } from '../types';
import * as HTMLElementCreator from '../../../../libs/HTML/HTMLElementCreator';
import { EditorGoalsPanelItem } from './EditorGoalsPanelItem';
import type { LevelTask } from '../../../../game/tasks/types';
import { LevelTaskType } from '../../../../game/tasks/types';
import { removeElement } from '../../../../utils/array-utils';
import { SECTION_LABEL_STYLE } from '../../EditorOverlayStyles';

export class EditorGoalsPanel implements EditorPanel {
  public readonly element;

  private readonly goalsElement: HTMLDivElement;

  private readonly goalItems: EditorGoalsPanelItem[] = [];

  constructor(private readonly onStateUpdate: EditorPanelStateChangeCallback) {
    this.element = HTMLElementCreator.createDiv({
      parentElement: undefined,
      style: {
        color: 'black',
        minWidth: '180px',
        minHeight: '200px',
        marginTop: '5px',
        pointerEvents: 'auto',
      },
    });

    HTMLElementCreator.createLabel({
      parentElement: this.element,
      labelText: 'Goals',
      style: { ...SECTION_LABEL_STYLE },
    });

    HTMLElementCreator.createButton({
      parentElement: this.element,
      labelText: 'Add Goal',
      onClick: () => {
        this.addGoal({
          type: LevelTaskType.Hexes,
          amount: 100,
        });
        this.updateState();
      },
      style: {
        marginTop: '5px',
        backgroundColor: '#0055ff',
        color: 'white',
        fontSize: '13px',
      },
    });

    this.goalsElement = HTMLElementCreator.createDiv({
      parentElement: this.element,
      style: {
        overflowX: 'hidden',
        overflowY: 'scroll',
        minHeight: '250px',
        maxHeight: '250px',
        paddingRight: '3px',
        scrollbarWidth: 'thin',
      },
    });
  }

  public destroy(): void {
    this.element.remove();
  }

  public focus(): void {
    this.element.style.visibility = 'visible';
  }

  public blur(): void {
    this.element.style.visibility = 'hidden';
  }

  public update(goalData: number | LevelTask[]): void {
    this.clear();

    const goals = Array.isArray(goalData)
      ? goalData
      : [
          {
            type: LevelTaskType.Hexes,
            amount: goalData,
          },
        ];

    goals.forEach((goal) => {
      this.addGoal(goal);
    });
  }

  private addGoal(goal: LevelTask): void {
    const onGoalDeleteRequest = (item: EditorGoalsPanelItem): void => {
      if (this.goalItems.length <= 1) {
        // eslint-disable-next-line no-alert
        alert('You must have at least 1 active goal.');
        return;
      }
      this.deleteItem(item);
      this.updateState();
    };

    const item = new EditorGoalsPanelItem(goal, this.updateState.bind(this), onGoalDeleteRequest);
    this.goalsElement.appendChild(item.element);
    this.goalItems.push(item);
  }

  private deleteItem(item: EditorGoalsPanelItem): void {
    removeElement(this.goalItems, item);
    item.destroy();
  }

  private clear(): void {
    this.goalItems.slice().forEach((item) => {
      this.deleteItem(item);
    });
  }

  private updateState(): void {
    this.onStateUpdate({
      goal: this.goalItems.map((item) => item.export()),
    });
  }
}
